/**
 * Execution channels + tool-surface categories — the shared vocabulary for
 * per-channel model routing and tool scoping. Value lists are the SSOT; the
 * types derive from them so adding a channel/category reaches every consumer.
 */

/** Execution channels — every place a model turn originates.
 *  Value list is the single source of truth; the type is derived from it, so
 *  adding a channel here reaches every consumer (situation block, config
 *  routes) with no hand-maintained duplicate list to drift. */
export const CHANNEL_NAMES = ['dashboard', 'summarize', 'routine', 'subagent'] as const;
export type ChannelName = (typeof CHANNEL_NAMES)[number];

/** Whether `name` is a channel (a route's `:channel`, a stored key). */
export function isChannelName(name: string): name is ChannelName {
  return (CHANNEL_NAMES as readonly string[]).includes(name);
}

/**
 * Tool-surface categories — RELEVANCE only: which tools a sub-agent is shown
 * (`channelToolCategories.subagent`, default below), and `email` is offered to
 * the main agent only when AgentMail is set up (`src/api/chat-tools.ts`).
 * Safety is not a category's job: `mutates` (below) is the floor that keeps
 * every tool that can change something away from sub-agents. Value list is
 * the SSOT; the type is derived, same pattern as {@link CHANNEL_NAMES}.
 *
 * - `core`: memory, knowledge, projects, skills, files, indexed docs.
 * - `data`: calendar, contacts, routines, past conversations.
 * - `code`: code navigation, lint/type checks, git, tests.
 * - `web`: the web — web_search, web_read, web_screenshot and browser
 *   sessions.
 * - `diagnostics`: how OneStone itself is running — logs, the tool-call log,
 *   analytics, status of data/scheduler/providers/external MCP, settings.
 *   Read-only research a worker auditing or debugging OneStone needs.
 * - `email`, `desktop`: the inbox, and the operator's live screen.
 * - `orchestrator`: the operator's account and the agent's own identity,
 *   credentials, and acts only the main agent takes (`ask_operator`,
 *   archiving a conversation).
 * - `external`: tools from connected external MCP servers (ADR-0006). Their
 *   records are generated when a server connects; one counts as read-only
 *   only when the operator trusts its server and the tool says `readOnlyHint`.
 */
export const TOOL_CATEGORIES = [
  'core',
  'data',
  'code',
  'web',
  'diagnostics',
  'email',
  'desktop',
  'orchestrator',
  'external',
] as const;
export type ToolCategory = (typeof TOOL_CATEGORIES)[number];

/**
 * MCP tool annotations (spec 2025-11-25 `ToolAnnotations`) — hints describing a
 * tool's effect on its environment, served in `tools/list`. Typed so the two
 * hints the spec calls "meaningful only when `readOnlyHint == false`" can only
 * be set on tools that modify something; every hint is explicit because the
 * spec's defaults (`destructiveHint: true`, `openWorldHint: true`) would
 * otherwise describe most OneStone tools wrongly.
 * - `readOnlyHint`: the tool does not modify its environment.
 * - `destructiveHint`: it may overwrite or delete (vs. only adding).
 * - `idempotentHint`: repeating the call with the same arguments has no further effect.
 * - `openWorldHint`: it reaches entities outside OneStone and the local machine (web, email, external APIs, arbitrary commands).
 */
export type ToolAnnotations =
  | { readOnlyHint: true; openWorldHint: boolean }
  | { readOnlyHint: false; destructiveHint: boolean; idempotentHint: boolean; openWorldHint: boolean };

/**
 * The catalog facts every tool record declares next to its schema (ADR-0004):
 * nothing about a tool is declared anywhere else.
 * - `category`: relevance — which sub-agents are shown the tool (see
 *   {@link TOOL_CATEGORIES}). Never the safety rule; that is `mutates`.
 * - `mutates`: the tool can change something, so read-only sub-agents never
 *   get it — they research and report, the orchestrator makes every change.
 *   That covers OneStone stores, identity and AgentMail; files and anything a
 *   shell or the project's own code can do (`bash`, `write_file`, `test_run`);
 *   processes; actions on web pages. Also the agency gate's "needs approval
 *   under guided" input. Distinct from `annotations`, which describe effects on
 *   the environment in MCP terms.
 * - `orchestratorOnly`: never offered to a sub-agent, whatever its effects —
 *   `spawn_agent` (workers can't spawn workers) and `ask_operator` (workers
 *   can't block on the operator). Kept apart from `mutates` so a tool that
 *   changes nothing can say so: `readOnlyHint` is what lets the Claude CLI run
 *   several calls of a tool at once (MA-469).
 * - `annotations`: MCP tool annotations.
 * - `requireApproval`: ask the operator before every interactive call by
 *   default. A saved Settings → Tools policy overrides it either way.
 */
export interface ToolCatalogFields {
  category: ToolCategory;
  mutates: boolean;
  orchestratorOnly?: true;
  annotations: ToolAnnotations;
  requireApproval?: true;
}

/**
 * Default sub-agent tool categories — ships as the visible Settings default,
 * not just a silent fallback (operator can widen/narrow via
 * `channelToolCategories.subagent`). Sub-agents are read-only research and
 * analysis workers, so every research read is in: knowledge and past
 * conversations, code and git, the web, OneStone's own logs and status, and
 * the read-only tools of trusted external MCP servers.
 * Out: `email` (the inbox), `desktop` (the operator's live screen) and
 * `orchestrator` (the operator's account, the agent's identity, credentials).
 * Dispatch holds a sub-agent to exactly this list (`isOnSubAgentSurface`).
 */
export const DEFAULT_SUBAGENT_TOOL_CATEGORIES: ToolCategory[] = [
  'core',
  'data',
  'code',
  'web',
  'diagnostics',
  'external',
];

/** Which engine runs a channel's turns: 'cli' = the Claude Code subscription
 *  CLI, 'api' = a provider API. */
export type ChannelExecutor = 'cli' | 'api';
